import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  OnInit,
  Input,
} from '@angular/core';
import { PresenceStateDto } from '@app/presence/models';
import { PresenceService } from '@app/presence/services';
import { map, scan, shareReplay, switchMap, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { isVariableStatement } from 'typescript';
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { TeleworkerState } from '@app/core/models';
import { CoreService, UserProfileService } from '@app/core/services';
import { Router } from '@angular/router';
import { SchedCheckService, WorkService } from '../services';
import { DiallerService } from '@app/dialler/services';
import { AuthService, shareReplayRefCount, Unity4Claim } from '@adreno-common';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  merge,
  of,
  scheduled,
} from 'rxjs';
import { LateShiftReason } from '../models';

@Component({
  selector: 'app-unavailable-presence-state',
  templateUrl: './unavailable-presence-state.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnavailablePresenceStateComponent implements OnInit {
  @Input()
  modalRef?: any;

  private currentShifts$!: Observable<LateShiftReason[] | undefined>;
  private showEarlyReasons$ = new BehaviorSubject<boolean>(false);

  Unity4Claim = Unity4Claim;

  viewModel$!: Observable<ViewModel>;

  constructor(
    private presenceSvc: PresenceService,
    private activeModal: NgbActiveModal,
    private workSvc: WorkService,
    private diallerSvc: DiallerService,
    private authSvc: AuthService,
    private upSvc: UserProfileService,
    private schedSvc: SchedCheckService
  ) {}

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.workSvc.hasIBScheduled$.pipe(
        map((ibScheduled) => ({ ibScheduled }))
      ),
      this.presenceSvc.states$.pipe(
        map((presenceStates) => ({ presenceStates }))
      ),
      combineLatest([
        this.upSvc.userProfile$,
        this.authSvc.hasClaim$(Unity4Claim.ScheduleCheck),
        this.currentShifts$,
      ]).pipe(
        map(([up, hc, currentShifts]) => ({
          showLeavingEarly:
            hc &&
            up.country == 'Australia' &&
            currentShifts &&
            currentShifts.length > 0,
        }))
      ),
      this.showEarlyReasons$.pipe(
        map((showEarlyReasons) => ({ showEarlyReasons }))
      ),
      of(earlyReasons).pipe(map((earlyReasons) => ({ earlyReasons })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  ngOnInit(): void {
    this.currentShifts$ = this.workSvc.campaignId$.pipe(
      switchMap((campaignId) => this.schedSvc.currentShifts$(campaignId || 0)),
      shareReplayRefCount()
    );
    this.initViewModel();
  }

  routeToPortal() {
    this.workSvc.backToPortal();
    this.activeModal.close('Close click');
  }

  selectIBTransferOnly() {
    this.diallerSvc.inboundTransferOnly();
    this.activeModal.close();
  }

  selectLeavingEarly(reason: string) {
    this.currentShifts$
      .pipe(
        take(1),
        switchMap((currentShifts) => {
          if (!currentShifts || currentShifts.length == 0) return of(void 0);
          const currentShift = currentShifts[0];
          return this.schedSvc.logReason({
            ...currentShift,
            reason,
          });
        })
      )
      .subscribe(() => this.routeToPortal());
  }

  selectState(state: PresenceStateDto) {
    this.activeModal.close();
    this.presenceSvc.addStateMapping(TeleworkerState.Unavailable, state.id);
    this.diallerSvc.nailInCallLostState = undefined;
    this.workSvc.backToUnloaded();
  }

  public set showEarlyReasons(show: boolean) {
    this.modalRef._windowCmptRef.instance.size = 'md';
    this.showEarlyReasons$.next(show);
  }
}

interface ViewModel {
  earlyReasons?: string[];
  ibScheduled?: boolean;
  presenceStates?: PresenceStateDto[];
  showEarlyReasons?: boolean;
  showLeavingEarly?: boolean;
}

const earlyReasons = [
  'I am unwell',
  'I have to go to an appointment',
  "I've found it hard going today and need to log off early",
  'I have an Internet/Power Outage',
  'My campaign is out of records or experiencing a problem',
  'I have caregiver responsibilities',
  'Other',
];
